/**
 * UserDrilldownPage — `/user/:username` drill-down (milestone v1.4 / Phase 4
 * Plan 09; redesigned in v1.4 L6, unified-activities-table in Phase 8).
 *
 * Phase 8 Plan 04 changes:
 *   - Activity tab now consumes the shared `<TraceTable expandable>` driven
 *     by `useTracesList({...filters, users:[username]})` + `ActivityToolbar`
 *     + `useActivityUrlState`. Same component as the Overview / Quality /
 *     Operations modal drill-downs — one source of truth for trace tables.
 *   - Feedback tab DELETED. Rated-trace triage moves into the Activity tab
 *     (toolbar `Rating: rated` filter). Legacy `?tab=feedback` URLs redirect
 *     to `?tab=activity&rating=rated` via a `useEffect` so deep links and
 *     bookmarks survive.
 *   - Backend: the per-user activity-feed and feedback-timeline routes are
 *     gone. The page uses `/salescoach/api/traces/list` exclusively.
 *
 * v1.4 L6 changes preserved: ratings count in the KPI strip, back button
 * with scroll restoration, safety-refusals panel inside Activity.
 *
 * Route-param consistency (WARNING-12 / F-02): this page and every link
 * to it use `:username`. Hook fetchers path their URLs on
 * `encodeURIComponent(username)` end-to-end.
 */

import * as React from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ActivityToolbar } from '@/components/ActivityToolbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SegmentedControl } from '@/components/ui/segmented';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ChartTooltip, useChartTooltipTrigger } from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  sumField,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import { KPICard } from '@/components/KPICard';
import { RatingScatter } from '@/components/RatingScatter';
import {
  SortableTable,
  type SortableColumn,
} from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import {
  TraceTable,
  type TraceColumnKey,
  type TraceTableSort,
  type TraceSortKey,
} from '@/components/TraceTable';
import { RecountNotice } from '@/components/RecountNotice';
import { UserProfileHeader } from '@/components/UserProfileHeader';
import { useActivityUrlState } from '@/hooks/use-activity-url-state';
import type {
  RestoreScrollLocationState,
  UserDrilldownLocationState,
} from '@/hooks/use-open-user-detail';
import { useTracesList } from '@/hooks/use-traces-list';
import { useUrlFilters } from '@/hooks/use-url-filters';
import {
  UserNotFoundError,
  useUserCharts,
  useUserKpis,
  useUserProfile,
  useUserSafetyRefusals,
  useUserTopSkills,
  useUserTopTools,
} from '@/hooks/use-user-drilldown';
import { chartPalette } from '@/lib/chart-palette';
import { formatDateShort, formatRelativeCet } from '@/lib/format-date';
import { tracelistRowToTableRow } from '@/lib/trace-row';
import type {
  TraceListSort,
  UserChartsSource,
  UserCostPoint,
  UserSkillRow,
  UserToolRow,
  UserTrendPoint,
} from '@/services/api-types';

import { LANGFUSE_TRACE_BASE } from '@/lib/langfuse';

// ---------------------------------------------------------------------------
// Inline retry card — mirrors OperationsPage / QualityPage's convention
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatBucketLabel(bucket: string): string {
  return formatDateShort(bucket);
}

function formatRelative(iso: string): string {
  return formatRelativeCet(iso);
}

const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const NUM_FMT = new Intl.NumberFormat('en-US');

// ---------------------------------------------------------------------------
// Small chart components — requests trend + cost trend (rating uses the
// dedicated RatingScatter component)
// ---------------------------------------------------------------------------

// Source-split palette (Agent C / Batch 6). Reactive keeps the chart's
// historical neutral blue so the default `all` view looks unchanged when
// briefs are absent (which is the current DB state — see computeUserCharts
// for the rationale). Briefs pick up amber, matching the Briefs page's
// warm-on-neutral split and giving strong contrast vs blue. Sourced from
// chart-palette SSOT so every chart speaks the same colour language.
const REQUESTS_BRIEFS_COLOR = chartPalette.briefs;
const REQUESTS_REACTIVE_COLOR = chartPalette.reactive;

function RequestsTrendChart({
  data,
  loading,
  source,
  height = 240,
}: {
  data: UserTrendPoint[];
  loading: boolean;
  source: UserChartsSource;
  height?: number;
}): React.ReactElement {
  const tooltipTrigger = useChartTooltipTrigger();
  if (loading) {
    return (
      <div
        data-testid="user-requests-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="user-requests-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No activity in selected period
      </div>
    );
  }
  // When the user narrows to one side, hide the zeroed series entirely
  // (don't render a flat line at y=0). `all` view shows both.
  const showBriefs = source !== 'reactive';
  const showReactive = source !== 'briefs';
  return (
    <div
      data-testid="user-requests-trend"
      data-source={source}
      style={{ width: '100%', height }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v)}
          />
          <YAxis allowDecimals={false} />
          <Tooltip
            {...tooltipTrigger}
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
            content={
              <ChartTooltip
                labelFormatter={(v) =>
                  typeof v === 'string' ? formatBucketLabel(v) : String(v ?? '')
                }
              />
            }
          />
          {showReactive && (
            <Line
              type="monotone"
              dataKey="reactive"
              stroke={REQUESTS_REACTIVE_COLOR}
              strokeWidth={2}
              dot={false}
              name="Reactive"
              isAnimationActive={false}
              data-testid="user-requests-trend-line-reactive"
            />
          )}
          {showBriefs && (
            <Line
              type="monotone"
              dataKey="briefs"
              stroke={REQUESTS_BRIEFS_COLOR}
              strokeWidth={2}
              dot={false}
              name="Briefs"
              isAnimationActive={false}
              data-testid="user-requests-trend-line-briefs"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CostTrendChart({
  data,
  loading,
  height = 240,
}: {
  data: UserCostPoint[];
  loading: boolean;
  height?: number;
}): React.ReactElement {
  const tooltipTrigger = useChartTooltipTrigger();
  if (loading) {
    return (
      <div
        data-testid="user-cost-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="user-cost-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No cost recorded
      </div>
    );
  }
  return (
    <div data-testid="user-cost-trend" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v)}
          />
          <YAxis
            tickFormatter={(v: number) => CURRENCY_FMT.format(v)}
            width={70}
          />
          <Tooltip
            {...tooltipTrigger}
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
            content={
              <ChartTooltip
                labelFormatter={(v) =>
                  typeof v === 'string' ? formatBucketLabel(v) : String(v ?? '')
                }
                valueFormatter={(v, name) =>
                  `${name ?? 'Cost'}: ${CURRENCY_FMT.format(Number(v ?? 0))}`
                }
              />
            }
          />
          <Line
            type="monotone"
            dataKey="cost"
            stroke={chartPalette.cost}
            strokeWidth={2}
            dot={false}
            name="Cost"
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Back button — reads location.state set by `useOpenUserDetail`, persisted
// in sessionStorage so it survives in-page `setSearchParams` pushes (those
// create new history entries with empty state). Without persistence the
// second filter/tab change drops returnTo and Back walks the history one
// step at a time, looking like ctrl+z. Falls back to Engagement when no
// state is present (deep link / direct refresh).
// ---------------------------------------------------------------------------

const sessionReturnKey = (username: string): string =>
  `salescoach:user-return:${encodeURIComponent(username)}`;

function BackButton(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const state = (location.state ?? null) as UserDrilldownLocationState | null;

  React.useEffect(() => {
    if (!username) return;
    if (state?.returnTo) {
      try {
        sessionStorage.setItem(
          sessionReturnKey(username),
          JSON.stringify({
            returnTo: state.returnTo,
            returnScrollY: state.returnScrollY ?? 0,
          }),
        );
      } catch {
        // sessionStorage may be unavailable (private mode, quota); the
        // single-shot in-memory state still works for the first Back click.
      }
    }
  }, [username, state]);

  const stored = React.useMemo<UserDrilldownLocationState | null>(() => {
    if (state?.returnTo) return state;
    if (!username) return null;
    try {
      const raw = sessionStorage.getItem(sessionReturnKey(username));
      return raw ? (JSON.parse(raw) as UserDrilldownLocationState) : null;
    } catch {
      return null;
    }
  }, [state, username]);

  const returnTo = stored?.returnTo ?? null;
  const returnScrollY = stored?.returnScrollY ?? null;

  const label = returnTo
    ? (() => {
        // Trim search params for the label so users see a clean section
        // name, not "engagement?preset=7d".
        const path = returnTo.split('?')[0] ?? returnTo;
        const name = path.replace(/^\//, '');
        if (!name) return 'Back';
        const friendly = name.charAt(0).toUpperCase() + name.slice(1);
        return `Back to ${friendly}`;
      })()
    : 'Back';

  const handleClick = (): void => {
    if (returnTo) {
      const restoreState: RestoreScrollLocationState = {
        restoreScrollY: returnScrollY ?? 0,
      };
      navigate(returnTo, { state: restoreState });
    } else {
      // No remembered source → bounce to Engagement (primary entry).
      // Avoids `navigate(-1)` because that walks the in-page setSearchParams
      // history one step per click (the ctrl+z bug).
      navigate('/engagement');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="user-drilldown-back"
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline min-h-11 py-2"
    >
      <span aria-hidden>←</span>
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Activity feed pagination.
//
// 50 rows per page (matches the Phase 8 Plan 04 default for the shared
// `<TraceTable>`). Server-paginated via `useTracesList(filters, {limit, offset})`
// → `/api/traces/list`. The Feedback timeline tab was removed in Phase 8;
// its rated-trace triage is now folded into Activity via the toolbar's
// rating filter.
// ---------------------------------------------------------------------------

const ACTIVITY_PAGE = 50;

const ACTIVITY_COLUMNS: TraceColumnKey[] = [
  'time',
  'kind',
  'rating',
  'latency',
  'cost',
  'queryAnswer',
  'feedback',
];

/**
 * Map the server-side sort enum (`TraceListSort`, e.g. `'rating_desc'`)
 * back to the in-table {key, dir} shape that `<TraceTable>` consumes for
 * its header arrows.
 *
 * Activity-tab columns and their `TraceSortKey` values are constrained to:
 *   timestamp / name / rating / latencyMs / cost
 * (see `ACTIVITY_COLUMNS` + `COLUMN_CONFIG` in `TraceTable.tsx`). Anything
 * outside that set means the URL was hand-crafted with a sort the toolbar
 * cannot produce — fall back to the default desc-by-time so the header
 * arrows still render somewhere sensible.
 */
function traceListSortToTableSort(s: TraceListSort): TraceTableSort {
  switch (s) {
    case 'oldest':
      return { key: 'timestamp', dir: 'asc' };
    case 'rating_desc':
      return { key: 'rating', dir: 'desc' };
    case 'rating_asc':
      return { key: 'rating', dir: 'asc' };
    case 'latency_desc':
      return { key: 'latencyMs', dir: 'desc' };
    case 'latency_asc':
      return { key: 'latencyMs', dir: 'asc' };
    case 'cost_desc':
      return { key: 'cost', dir: 'desc' };
    case 'cost_asc':
      return { key: 'cost', dir: 'asc' };
    case 'name_desc':
      return { key: 'name', dir: 'desc' };
    case 'name_asc':
      return { key: 'name', dir: 'asc' };
    case 'newest':
    default:
      return { key: 'timestamp', dir: 'desc' };
  }
}

/**
 * Inverse of {@link traceListSortToTableSort}. Returns null when the column
 * the user clicked has no server-side sort counterpart (Activity tab never
 * shows such columns today, but we keep the mapping defensive so a future
 * column addition doesn't silently no-op the click).
 */
function tableSortToTraceListSort(
  key: TraceSortKey,
  dir: 'asc' | 'desc',
): TraceListSort | null {
  switch (key) {
    case 'timestamp':
      return dir === 'asc' ? 'oldest' : 'newest';
    case 'rating':
      return dir === 'asc' ? 'rating_asc' : 'rating_desc';
    case 'latencyMs':
      return dir === 'asc' ? 'latency_asc' : 'latency_desc';
    case 'cost':
      return dir === 'asc' ? 'cost_asc' : 'cost_desc';
    case 'name':
      return dir === 'asc' ? 'name_asc' : 'name_desc';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function UserDrilldownPage(): React.ReactElement {
  // WARNING-12 / F-02 — param name is `username`, not `id`.
  const { username } = useParams<{ username: string }>();

  // Drill-down should always open at the top — the source page (Engagement)
  // restores its own scroll on Back via useRestoreScroll, so this only
  // affects forward navigation into the drill-down.
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [username]);

  const { filters } = useUrlFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 8 — legacy `?tab=feedback` redirect. The Feedback tab is gone;
  // bookmarks land on Activity with the rated filter pre-applied so users
  // see the rated-trace subset they came for. `replace:true` keeps the back
  // button useful (CONTEXT C-4) and `feedback_expanded` is the only stale
  // legacy URL key we need to purge — the new Activity tab uses
  // `activity_expanded` (owned by `useActivityUrlState`).
  React.useEffect(() => {
    if (searchParams.get('tab') !== 'feedback') return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'activity');
        next.set('rating', 'rated');
        next.delete('feedback_expanded');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Phase 8 Plan 05 — URL-controlled Tabs value. `?tab=overview` (or no
  // `tab=` param) shows Overview, `?tab=activity` shows Activity. Anything
  // else falls back to overview so a malformed deep link doesn't render a
  // blank panel. The legacy `?tab=feedback` redirect above replaces the URL
  // with `tab=activity` BEFORE this read settles, so the controlled value
  // sees the rewritten URL on the next render.
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'activity' ? 'activity' : 'overview';
  const onTabChange = React.useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'overview') {
            // Default tab — drop the param to keep the URL clean.
            params.delete('tab');
          } else {
            params.set('tab', next);
          }
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // Server-paged activity feed. Reset to page 1 whenever filters change so
  // the user doesn't land on "page 5 of 2" after narrowing the date range.
  const [activityPage, setActivityPage] = React.useState<number>(1);
  /* Whether this feed has ever had more than one page. The page count is
     derived from a payload that goes away on every page change, so the row
     that shows it has to be reserved from what was already true — the same
     way KPICard reserves a sublabel row it has already carried.
     Latched, never cleared. Counting shifts of this row per filter change:
     many pages -> many pages costs 0 latched and 2 cleared; many -> one costs
     1 either way; one -> one costs 2 latched (the row appears with a skeleton
     counter and then goes) and 0 cleared. So latching wins on the common case
     and loses on a single-page feed once it has ever paginated — the second is
     what remains, and it is a phantom Prev/Next rather than a lost one. */
  const hasPaginatedRef = React.useRef(false);
  React.useEffect(() => {
    setActivityPage(1);
  }, [filters]);

  // Requests-trend source filter (Agent C / Batch 6). Per-drill-down local
  // state — we deliberately DON'T push it into the URL: the filter is a
  // chart-local knob, not a cross-tab contract, and URL-persisting it would
  // bloat the filter schema every other page consumes. `all` is the
  // default so users see both series on mount.
  const [chartSource, setChartSource] =
    React.useState<UserChartsSource>('all');

  // Phase 8 — Activity tab URL state (rating select / search / errorsOnly /
  // expanded set). Owned by `useActivityUrlState`; the toolbar wires
  // setters directly. `toApiExtras()` translates URL state into the
  // `useTracesList` extras shape (rating-enum → ratingMin/Max or
  // hasFeedback, plus search/errorsOnly).
  const activity = useActivityUrlState();

  // Reset to page 1 when toolbar filters move — same rationale as the
  // global-filters reset above. `rating`/`search`/`errorsOnly` are the
  // toolbar's three knobs; `sort` is the column-header click — flipping
  // sort shuffles the row order entirely, so "page 5 of newest" and
  // "page 5 of oldest" are completely different windows.
  React.useEffect(() => {
    setActivityPage(1);
  }, [activity.rating, activity.search, activity.errorsOnly, activity.sort]);

  // Phase 8 — scope the global Filters payload to this user. The hook
  // already supports `users:[username]`; we override locally so toolbar
  // toggles never leak across drill-downs.
  //
  // `admins: 'included'` (2026-08-19) for the same reason the six panels
  // above pass `USER_DRILLDOWN_FILTERS` (queries.ts): this page is one named
  // person's record, and the global Admins filter chooses a POPULATION, which
  // a `users:[…]` scope has already chosen. Without it an admin's page shows
  // his KPIs, charts and top-tools while the Activity list underneath them
  // renders empty — the same contradiction as the 404, only quieter. It can
  // only ever change what this request returns when `username` IS an admin.
  const scopedFilters = React.useMemo(
    () => ({ ...filters, admins: 'included' as const, users: [username ?? ''] }),
    [filters, username],
  );

  const tracesQuery = useTracesList(
    scopedFilters,
    {
      ...activity.toApiExtras(),
      limit: ACTIVITY_PAGE,
      offset: (activityPage - 1) * ACTIVITY_PAGE,
    },
    /* enabled */ Boolean(username),
  );

  // ------ Remaining hook calls ------
  /* Recorded AFTER a render, never during one: this is a note about what the
     last settled response said, and a ref written mid-render is a value a
     discarded render can leave behind. */
  React.useEffect(() => {
    if (tracesQuery.isLoading) return;
    if ((tracesQuery.data?.total ?? 0) > ACTIVITY_PAGE) {
      hasPaginatedRef.current = true;
    }
  }, [tracesQuery.isLoading, tracesQuery.data]);

  const profile = useUserProfile(username);
  const kpis = useUserKpis(username, filters);
  const charts = useUserCharts(
    username,
    filters,
    filters.granularity,
    chartSource,
  );
  const topTools = useUserTopTools(username, filters, { limit: 5 });
  const topSkills = useUserTopSkills(username, filters, { limit: 5 });
  const refusals = useUserSafetyRefusals(username, filters);

  // Map TraceListRow → TraceTableRow (shared mapper used by all three
  // call sites — see `lib/trace-row.ts`).
  const activityRows = React.useMemo(
    () => (tracesQuery.data?.rows ?? []).map(tracelistRowToTableRow),
    [tracesQuery.data],
  );

  // Server-side sort header — clicking a sortable column flips direction
  // when it's already active, otherwise switches column and starts at desc
  // (matches the modal-side `compareRows` convention so the cursor's
  // "obvious next click" gives the most useful first ordering for time /
  // rating / latency / cost). The mapping is server-paginated, so we
  // route through `activity.setSort` → URL → `useTracesList` extras
  // rather than sorting the in-memory page.
  const activitySort = React.useMemo(
    () => traceListSortToTableSort(activity.sort),
    [activity.sort],
  );
  const onActivitySort = React.useCallback(
    (key: TraceSortKey) => {
      const nextDir: 'asc' | 'desc' =
        activitySort.key === key && activitySort.dir === 'desc'
          ? 'asc'
          : activitySort.key === key
            ? 'desc'
            : 'desc';
      const next = tableSortToTraceListSort(key, nextDir);
      if (next) activity.setSort(next);
    },
    [activitySort, activity],
  );

  // ------ 404 early-return ------
  if (profile.isError && profile.error instanceof UserNotFoundError) {
    return (
      <section
        data-testid="user-not-found"
        aria-labelledby="user-not-found-heading"
        className="space-y-4"
      >
        <BackButton />
        <header>
          <h1
            id="user-not-found-heading"
            className="text-2xl font-semibold"
          >
            User not found
          </h1>
          <p className="text-sm text-muted-foreground">
            No traces on record for{' '}
            <span className="font-mono">{profile.error.username}</span>.
          </p>
        </header>
        <Link
          to="/engagement"
          className="text-sm underline text-primary hover:no-underline"
        >
          ← Back to Engagement
        </Link>
      </section>
    );
  }

  // Missing :username shouldn't happen in practice (React Router would
  // fall through to NotFoundPage), but guard for type-safety.
  if (!username) {
    return (
      <section className="space-y-4">
        <BackButton />
        <header>
          <h1 className="text-2xl font-semibold">User</h1>
          <p className="text-sm text-muted-foreground">
            Missing <span className="font-mono">username</span> parameter.
          </p>
        </header>
      </section>
    );
  }

  const k = kpis.data;

  // Top-tools / top-skills columns. Both tables pass getRowKey explicitly
  // (WARNING-09 — SortableTable requires it, no fallback).
  const toolColumns: Array<SortableColumn<UserToolRow>> = [
    { key: 'name', header: 'Tool' },
    { key: 'calls', header: 'Calls', align: 'right', sortable: true },
    {
      key: 'lastUsed',
      header: 'Last used',
      align: 'right',
      format: (v) => formatRelative(String(v)),
    },
  ];
  const skillColumns: Array<SortableColumn<UserSkillRow>> = [
    { key: 'name', header: 'Skill' },
    {
      key: 'activations',
      header: 'Activations',
      align: 'right',
      sortable: true,
    },
    {
      key: 'avgRating',
      header: 'Avg rating',
      align: 'right',
      format: (v) =>
        v === null || v === undefined ? '—' : `${(v as number).toFixed(1)}★`,
    },
  ];

  return (
    <section
      aria-labelledby="user-drilldown-heading"
      data-testid="user-drilldown-page"
      className="space-y-6"
    >
      <h1 id="user-drilldown-heading" className="sr-only">
        User drilldown
      </h1>

      {/* Row 0 — back button (v1.4 L6). Keeps a clear affordance for users
          coming from Engagement / Quality; if they got here by deep link
          it falls back to browser history. */}
      <div>
        <BackButton />
      </div>

      {/* Row 1 — profile header (time-independent) */}
      <TabErrorBoundary name="User profile">
        {profile.isError && !(profile.error instanceof UserNotFoundError) ? (
          <ErrorRetry
            label="User profile"
            onRetry={() => void profile.refetch()}
          />
        ) : profile.isLoading || !profile.data ? (
          <Card data-testid="user-profile-header-skeleton">
            <CardContent className="p-4">
              <div className="h-8 w-48 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-4 w-32 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ) : (
          <UserProfileHeader profile={profile.data} />
        )}
      </TabErrorBoundary>

      {/* Not `tracesQuery`: the activity feed is one card at the bottom of
          this page with its own skeleton and its own reserved pagination row,
          and turning ITS page recounts nothing above it. A line across the
          whole drill-down saying the numbers are being counted again, while
          every number above it sits there unchanged, is simply false. */}
      <RecountNotice queries={[kpis, charts, topTools, topSkills, refusals]} />

      {/* Tabs — Overview (KPIs+charts+top lists) | Activity. The Feedback
          tab was removed in Phase 8; rated-trace triage is folded into
          Activity via the toolbar's `Rating: rated` filter. */}
      <Tabs
        value={activeTab}
        onValueChange={onTabChange}
        className="w-full"
      >
        <TabsList
          data-testid="user-drilldown-tabs"
          className="grid w-full grid-cols-2 sm:w-auto sm:inline-grid"
        >
          <TabsTrigger value="overview" data-testid="user-drilldown-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="user-drilldown-tab-activity">
            Activity
          </TabsTrigger>
        </TabsList>

        {/* ================= Overview tab ================= */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          {/* Row 2 — 6-card KPI strip. Thumbs-up replaced by Ratings count
              (with a `N w/ text` sub-label). Mirrors the Overview KPI and
              is a more honest headline metric than 4-and-5-star count. */}
          <TabErrorBoundary name="User KPIs">
            {kpis.isError ? (
              <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <KPICard
                  label="Requests"
                  value={k?.requests.current ?? null}
                  delta={k?.requests.delta ?? null}
                  loading={kpis.isLoading}
                />
                <KPICard
                  label="Avg rating"
                  value={k?.avgRating.current ?? null}
                  format="rating"
                  delta={k?.avgRating.delta ?? null}
                  loading={kpis.isLoading}
                />
                <KPICard
                  label="Ratings"
                  value={k?.ratingsCount.current ?? null}
                  delta={k?.ratingsCount.delta ?? null}
                  loading={kpis.isLoading}
                  subLabel={
                    k
                      ? `${NUM_FMT.format(k.textFeedbackCount.current)} w/ text`
                      : undefined
                  }
                  tooltip="Total rated traces in the selected period. 'w/ text' is the subset carrying free-text feedback."
                />
                <KPICard
                  label="Cost"
                  value={k?.cost.current ?? null}
                  format="currency"
                  delta={k?.cost.delta ?? null}
                  inverted
                  loading={kpis.isLoading}
                />
                <KPICard
                  label="Safety refusals"
                  value={k?.safetyRefusals.current ?? null}
                  delta={k?.safetyRefusals.delta ?? null}
                  inverted
                  loading={kpis.isLoading}
                />
                <KPICard
                  label="Voice share"
                  value={
                    k?.voiceShare.current === undefined
                      ? null
                      : Number((k.voiceShare.current * 100).toFixed(1))
                  }
                  format="percent"
                  delta={k?.voiceShare.delta ?? null}
                  loading={kpis.isLoading}
                />
              </div>
            )}
          </TabErrorBoundary>

          {/* Row 3 — 3-column charts. Source segmented-control scopes the
              Requests trend chart only (ratings scatter + cost trend stay
              full-range so a user flipping the filter doesn't lose context
              on the neighbouring cards). */}
          <TabErrorBoundary name="User charts">
            {charts.isError ? (
              <ErrorRetry
                label="Charts"
                onRetry={() => void charts.refetch()}
              />
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-2 gap-y-2 space-y-0">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Requests trend
                    </CardTitle>
                    <SegmentedControl<UserChartsSource>
                      value={chartSource}
                      onChange={setChartSource}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'briefs', label: 'Briefs' },
                        { value: 'reactive', label: 'Reactive' },
                      ]}
                      ariaLabel="Requests trend source"
                      size="sm"
                      data-testid="user-requests-trend-source"
                    />
                    {/* v1.4 dashboard-ux-fix: per-series totals for the
                        active source filter. Drops the zeroed series so
                        the badge stays tight when the user narrows to one
                        side. `basis-full` moves the badge to its own row
                        below the title+segmented-control. */}
                    <TimelineTotalsBadge
                      data-testid="user-requests-trend-totals"
                      loading={charts.isLoading}
                      className="basis-full justify-end"
                      items={(() => {
                        const rows = charts.data?.requests ?? [];
                        const out: TimelineTotalItem[] = [];
                        if (chartSource !== 'briefs') {
                          out.push({
                            key: 'reactive',
                            label: 'Reactive',
                            color: REQUESTS_REACTIVE_COLOR,
                            value: sumField(rows, 'reactive'),
                          });
                        }
                        if (chartSource !== 'reactive') {
                          out.push({
                            key: 'briefs',
                            label: 'Briefs',
                            color: REQUESTS_BRIEFS_COLOR,
                            value: sumField(rows, 'briefs'),
                          });
                        }
                        return out;
                      })()}
                    />
                  </CardHeader>
                  <CardContent className="p-4">
                    <RequestsTrendChart
                      data={charts.data?.requests ?? []}
                      loading={charts.isLoading}
                      source={chartSource}
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-4 pb-0">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Ratings (per trace)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {charts.isLoading ? (
                      <div
                        data-testid="user-rating-scatter-skeleton"
                        className="w-full rounded bg-muted animate-pulse"
                        style={{ height: 240 }}
                      />
                    ) : (
                      <RatingScatter dots={charts.data?.ratings ?? []} />
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Cost trend
                    </CardTitle>
                    <TimelineTotalsBadge
                      data-testid="user-cost-trend-totals"
                      loading={charts.isLoading}
                      items={[
                        {
                          key: 'cost',
                          label: 'Cost',
                          color: chartPalette.cost,
                          format: 'currency',
                          value: sumField(charts.data?.cost ?? [], 'cost'),
                        },
                      ] satisfies TimelineTotalItem[]}
                    />
                  </CardHeader>
                  <CardContent className="p-4">
                    <CostTrendChart
                      data={charts.data?.cost ?? []}
                      loading={charts.isLoading}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </TabErrorBoundary>

          {/* Row 4 — 2-col top-tools / top-skills */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <TabErrorBoundary name="Top tools">
              {topTools.isError ? (
                <ErrorRetry
                  label="Top tools"
                  onRetry={() => void topTools.refetch()}
                />
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Top tools
                  </div>
                  <SortableTable<UserToolRow>
                    tableId="userTopTools"
                    columns={toolColumns}
                    rows={topTools.data ?? []}
                    getRowKey={(r) => r.name}
                    defaultSort={{ column: 'calls', dir: 'desc' }}
                    isLoading={topTools.isLoading}
                    emptyMessage="No tool calls in selected period"
                  />
                </div>
              )}
            </TabErrorBoundary>
            <TabErrorBoundary name="Top skills">
              {topSkills.isError ? (
                <ErrorRetry
                  label="Top skills"
                  onRetry={() => void topSkills.refetch()}
                />
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Top skills
                  </div>
                  <SortableTable<UserSkillRow>
                    tableId="userTopSkills"
                    columns={skillColumns}
                    rows={topSkills.data ?? []}
                    getRowKey={(r) => r.name}
                    defaultSort={{ column: 'activations', dir: 'desc' }}
                    isLoading={topSkills.isLoading}
                    emptyMessage="No skill activations in selected period"
                  />
                </div>
              )}
            </TabErrorBoundary>
          </div>
        </TabsContent>

        {/* ================= Activity tab ================= */}
        <TabsContent value="activity" className="mt-4 space-y-6">
          {/* Short intro so the tab answers "для чего мне это надо" — the
              feed is the single-place triage view for everything this user
              did in the window, including safety-refused prompts that the
              Overview tab hides. */}
          <Card data-testid="user-activity-intro">
            <CardContent className="p-4 space-y-1 text-sm">
              <div className="font-medium text-foreground">
                Per-trace activity
              </div>
              <p className="text-muted-foreground">
                The full trace log for the selected window — newest first,
                refused prompts kept in view, one-click expand shows timing,
                cost, complexity, skills, tools, and any error class. Filter by
                rating, search prompt/response text, or isolate errors via the
                toolbar above the table. This log includes Jira link-clicks and
                morning briefs, so its total is higher than the Requests KPI,
                which counts reactive messages only.
              </p>
              {/* The line is self-gated on data, so a filter change used to
                  take it off the card entirely and shorten the card under the
                  reader's cursor. Reserved instead: same height, no number. */}
              {/* BOTH queries, because the refusal clause hangs off this
                  line: with traces landed and refusals still counting, the
                  line would print a real, fresh total with no clause beside
                  it — which reads as "no safety refusals", a claim nobody
                  has checked yet. */}
              {!tracesQuery.data || refusals.isLoading ? (
                <div
                  className="pt-1"
                  data-testid="user-activity-summary-skeleton"
                >
                  <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                </div>
              ) : (
                <div
                  className="pt-1 text-xs text-muted-foreground tabular-nums"
                  data-testid="user-activity-summary"
                >
                  {NUM_FMT.format(tracesQuery.data.total)} traces in window
                  {refusals.data && refusals.data.length > 0 && (
                    <>
                      {' · '}
                      <span className="text-red-700">
                        {NUM_FMT.format(refusals.data.length)} safety refusal
                        {refusals.data.length === 1 ? '' : 's'}
                      </span>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase 8 — shared <TraceTable expandable> driven by useTracesList.
              The toolbar (rating select / search / errorsOnly) is wired into
              `useActivityUrlState` so the URL mirrors filter state and rows
              expand inline (deep-linkable via `?activity_expanded`). */}
          <TabErrorBoundary name="Activity feed">
            <div className="space-y-2">
              {(() => {
                const total = tracesQuery.data?.total ?? 0;
                /* A page change re-keys the query, so `data` is away and
                   `total` reads 0 — which used to take the whole pagination
                   row off the page on every Next click, moving the bottom of
                   the table and making a second click impossible. The row is
                   held open once this feed has ever had more than one page;
                   `activityPage > 1` covers a deep link that lands on one. */
                const counting = tracesQuery.isLoading;
                const totalPages = Math.max(
                  1,
                  Math.ceil(total / ACTIVITY_PAGE),
                );
                const canPaginate =
                  totalPages > 1 ||
                  (counting && (hasPaginatedRef.current || activityPage > 1));
                const atFirst = activityPage <= 1;
                const atLast = activityPage >= totalPages;
                const rangeStart =
                  total === 0 ? 0 : (activityPage - 1) * ACTIVITY_PAGE + 1;
                const rangeEnd = Math.min(
                  activityPage * ACTIVITY_PAGE,
                  total,
                );
                /* `total` is 0 both when the window is empty and when it is
                   not counted yet, and the second one must not read as the
                   first — nor take the line off the page and move the table
                   up. Reserved with the same skeleton idiom. */
                const summary = counting ? (
                  <span
                    data-testid="activity-feed-range-skeleton"
                    className="inline-block h-4 w-32 rounded bg-muted animate-pulse"
                  />
                ) : total > 0 ? (
                  <span
                    className="text-xs text-muted-foreground tabular-nums"
                    data-testid="activity-feed-range"
                  >
                    Showing {rangeStart}–{rangeEnd} of {total}
                  </span>
                ) : null;
                const pagination = canPaginate ? (
                  <div
                    data-testid="activity-feed-pagination"
                    className="flex items-center justify-between pt-2 text-sm"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setActivityPage((p) => Math.max(1, p - 1))
                      }
                      /* `counting` as well: while the page count is
                         unknown, neither button may take a click. (On Next,
                         `atLast` happens to cover it — `totalPages` is 1 with
                         no payload — but that is a coincidence of the blanking
                         rule, not a decision either button should rest on.) */
                      disabled={atFirst || counting}
                      data-testid="activity-feed-prev"
                      className="min-h-[44px] min-w-[44px] px-3 py-2 font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline sm:min-h-0 sm:min-w-0 sm:px-2 sm:py-1"
                    >
                      ← Prev
                    </button>
                    {counting ? (
                      <span
                        data-testid="activity-feed-page-counter-skeleton"
                        className="inline-block h-4 w-24 rounded bg-muted animate-pulse"
                      />
                    ) : (
                      <span
                        className="text-xs text-muted-foreground tabular-nums"
                        data-testid="activity-feed-page-counter"
                      >
                        Page {activityPage} of {totalPages}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setActivityPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={atLast || counting}
                      data-testid="activity-feed-next"
                      className="min-h-[44px] min-w-[44px] px-3 py-2 font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline sm:min-h-0 sm:min-w-0 sm:px-2 sm:py-1"
                    >
                      Next →
                    </button>
                  </div>
                ) : null;
                return (
                  <div data-testid="activity-feed" className="space-y-2">
                    {summary}
                    <TraceTable
                      rows={activityRows}
                      columns={ACTIVITY_COLUMNS}
                      sort={activitySort}
                      onSort={onActivitySort}
                      from={filters.from ?? undefined}
                      to={filters.to ?? undefined}
                      isLoading={tracesQuery.isLoading}
                      isError={tracesQuery.isError}
                      onRetry={() => void tracesQuery.refetch()}
                      emptyMessage="No activity in selected period."
                      expandable
                      expandedIds={activity.expandedSet}
                      onToggleExpanded={activity.toggleExpanded}
                      toolbarSlot={
                        <ActivityToolbar
                          rating={activity.rating}
                          onRatingChange={activity.setRating}
                          search={activity.search}
                          onSearchChange={activity.setSearch}
                          errorsOnly={activity.errorsOnly}
                          onErrorsOnlyChange={activity.setErrorsOnly}
                        />
                      }
                      testIdPrefix="user-activity"
                    />
                    {pagination}
                  </div>
                );
              })()}
            </div>
          </TabErrorBoundary>

          {/* Conditional safety-refusals panel lives inside the Activity
              tab so related investigation surfaces sit together. */}
          {refusals.data && refusals.data.length > 0 && (
            <TabErrorBoundary name="Safety refusals">
              {refusals.isError ? (
                <ErrorRetry
                  label="Safety refusals"
                  onRetry={() => void refusals.refetch()}
                />
              ) : (
                <Card data-testid="user-safety-refusals-panel">
                  <CardHeader className="p-4 pb-0">
                    <CardTitle className="text-sm font-medium text-red-700">
                      Safety refusals ({refusals.data.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2">
                    {refusals.data.map((r) => (
                      <div
                        key={r.traceId}
                        data-testid="user-safety-refusal-row"
                        className="rounded border border-l-4 border-l-red-500 border-border bg-card p-3 text-xs"
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-muted-foreground">
                            {r.traceId}
                          </span>
                          <span className="text-muted-foreground">
                            {formatRelative(r.timestamp)}
                          </span>
                          <a
                            href={`${LANGFUSE_TRACE_BASE}${r.traceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline ml-auto"
                          >
                            → trace
                          </a>
                        </div>
                        {r.reason && (
                          <div className="mt-1 italic text-muted-foreground">
                            {r.reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabErrorBoundary>
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}
